import { computed } from 'vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useLocaleStore from '@/store/modules/locale.js';
import useIntegrationStore from '@/store/modules/integration.js';
import useAuthStore from '@/store/modules/auth.js';
import useRegisterStore from '@/store/modules/register.js';
import { useRoute } from 'vue-router';
import { useGtm } from '@gtm-support/vue-gtm';
import usePageNavigationHook from '@/hooks/usePageNavigation.js';
import useModalHook from '@/hooks/useModal.js';
import { buildUrlWithParams } from '@/helpers/common.js';
import { appFlowModeEnum } from '@/constants/appSettings.js';
import { routeIndexGroupNames } from '@/constants/route.js';
import { registerStepConfigurationPerRouteName } from '@/constants/register.js';

const useRegisterHook = (routeName = '') => {
  const appSettingsStore = useAppSettingsStore();
  const localeStore = useLocaleStore();
  const integrationStore = useIntegrationStore();
  const authStore = useAuthStore();
  const registerStore = useRegisterStore();

  const route = useRoute();
  const gtm = useGtm();
  const { redirectTo, indexRouteName } = usePageNavigationHook();
  const [isRegisterViaSmsModalOpen, openRegisterViaSmsModal, closeRegisterViaSmsModal] = useModalHook();

  const isIntegrationAsiacellIraq = computed(() => integrationStore.hasActiveIntegration &&
    (integrationStore.activeIntegration.name === integrationStore.availableIntegrations.ASIACELL_IRAQ.name),
  );
  const isHERegistration = computed(() =>
    integrationStore.hasActiveIntegration &&
    !isIntegrationAsiacellIraq.value &&  // should be different from ASIACELL_IRAQ
    appSettingsStore.appProperties.isSingleOptinEnabled &&
    registerStore.urlParams.heHash &&
    registerStore.urlParams.heMsisdn &&
    appSettingsStore.appFlowMode === appFlowModeEnum.GENERAL_SIMPLE_WITH_WEB_REGISTRATION,
  );
  const stepConfiguration = computed(() => {
    const configuration = registerStepConfigurationPerRouteName[routeName]?.[appSettingsStore.appFlowMode];

    if (!configuration) { return null; }

    const overrideConfiguration = {
      totalSteps: configuration.totalSteps,
      nextRouteName: configuration.nextRouteName,
    };

    if (isIntegrationAsiacellIraq.value) {
      overrideConfiguration.totalSteps = 2;
      if (configuration.step === overrideConfiguration.totalSteps) {
        overrideConfiguration.nextRouteName = routeIndexGroupNames.AUTH;
      }
    } else if (isHERegistration.value) {
      overrideConfiguration.totalSteps = 1;
      overrideConfiguration.nextRouteName = routeIndexGroupNames.AUTH;
    }

    return {
      ...configuration,
      ...overrideConfiguration,
    };
  });
  const currentStepNumber = computed(() => stepConfiguration.value?.step);
  const totalSteps = computed(() => stepConfiguration.value?.totalSteps);
  const isFinalStep = computed(() => (currentStepNumber.value > 0) && (currentStepNumber.value === totalSteps.value));
  const pageSubtitle = computed(() => isFinalStep.value ? 'page_register_subtitle_step_final' : `page_register_subtitle_step_${ totalSteps.value }`);

  const trackRegistrationEventToGTM = (step) => {
    gtm.trackEvent({
      event: 'CompleteRegistration',
      category: 'MSISDN Registration',
      action: `Register ${ step } registration`,
      label: `Register ${ step }`,
    });
  };

  const redirectToIndexStep = () => {
    if (isIntegrationAsiacellIraq.value) {
      window.location.href = buildUrlWithParams(
        `${ window.location.origin }/api/getRegisterUrl`,
        {
          refUrl: registerStore.urlParams.refUrl,
          clickid: registerStore.urlParams.clickId,
          publisherId: registerStore.urlParams.publisherId,
          campaignId: registerStore.urlParams.campaignId,
          friendRef: '', // facebook
          lang: localeStore.currentLanguage,
          msisdn: registerStore.urlParams.msisdn,
        },
      );

      return;
    }

    if (appSettingsStore.appFlowMode === appFlowModeEnum.TWO_STEP_SIMPLE_WITHOUT_WEB_REGISTRATION) {
      openRegisterViaSmsModal();

      return;
    }

    if (registerStore.registrationMsisdn) {
      registerStore.setRegistrationMsisdn('');
    }

    const canProceed = [
      appFlowModeEnum.GENERAL_DEFAULT,
      appFlowModeEnum.GENERAL_SIMPLE_WITH_WEB_REGISTRATION,
    ].includes(appSettingsStore.appFlowMode);

    redirectTo(canProceed ? 'RegisterAccountVerification' : indexRouteName.value);
  };

  const redirectToNextStep = () => {
    const nextRouteName = stepConfiguration.value?.nextRouteName;

    if (isFinalStep.value) {
      registerStore.setRegistrationMsisdn('');
      authStore.setLevelUp({ level: 1, prizeValue: null });
    }

    redirectTo(nextRouteName || routeIndexGroupNames.VISITOR);
  };

  const redirectToLoginPage = async () => {
    const loginRouteName = 'Login';
    const isRedirectionNeeded = route.name !== loginRouteName;

    await closeRegisterViaSmsModal({ immediate: isRedirectionNeeded });

    if (isRedirectionNeeded) {
      redirectTo(loginRouteName);
    }
  };

  return {
    isRegisterViaSmsModalOpen,
    isHERegistration,
    stepConfiguration,
    currentStepNumber,
    isFinalStep,
    pageSubtitle,
    isIntegrationAsiacellIraq,
    trackRegistrationEventToGTM,
    redirectToIndexStep,
    redirectToNextStep,
    redirectToLoginPage,
  };
};

export default useRegisterHook;
